/**
 * @typedef {import('./index.js').Store<State, Getters>} Store
 */
/**
 * @template T
 * @typedef {import('./index.js').GetterFunction<T, State, Getters>} GetterFunction<T>
 */
/**
 * @template T
 * @template R
 * @typedef {import('./index.js').ActionFunction<T, R, State, Getters>} ActionFunction<T,R>
 */
/**
 * @typedef {Object} Currency
 * @property {string} IsoCode - Currency ISO code
 * @property {string} Name - Currency name
 * @property {string} Symbol - Currency Symbol
 *
 * @typedef {Object} CurrencyQuery
 * @property {string} toCurrency - Currency to convert price
 * @property {string} fromCurrency - Currency to convert price from
 * @property {number} [price] - Price to convert
 * @property {boolean} [withIsoCode] - Include ISO Code information
 * @property {boolean} [isoAfter] - Include ISO Code information after the price
 * @property {boolean} [noSymbol] - Don't include symbol
 */
/**
 * @typedef {Object} RateResponse
 * @property {boolean} success - success from server
 * @property {number} timestamp
 * @property {string} base - base currency
 * @property {date} date - date of the rate
 * @property {RatesData} rates - rates returned from server
 */
/**
 * @typedef {Object<string,number>} RatesData
 */
/**
 * @typedef {Object<string,RatesData>} Rates
 */
import axios from 'axios'
import {formatCurrency, ensureNumber} from '@utils/currency-helpers'
import {logger} from '@plugins/logger'
import {getApiUrl} from '@utils/api-helpers'
/**
 * @module Store:Currency
 * @description Handles currency conversion on the whole application
 */
/**
 * Wait until currencyRatePending is done and resolve the promise
 * @param {State} state
 * @returns {Promise}
 */
const waitForRate = (state) => {
  return new Promise((resolve, reject) => {
    const interval = setInterval(() => {
      if (!state.currencyRatePending) {
        clearInterval(interval)
        resolve(state)
      }
    }, 1000)
  })
}
// Get external currency from input with data-currency property
const externalCurrency = document.querySelector('input[data-currency]') ?. value ?? 'USD'
// Get rates stored in localStorage
const storedRates = localStorage.getItem('rates')
// Get currencies stored in localStorage
const storedCurrencies = localStorage.getItem('currencies')
// Get selected currency stored in localStorage
const storedCurrency = localStorage.getItem('selected-currency')
// Get expiration date from localStorage
const ratesExp = localStorage.getItem('currency-exp')
const defaultRates = {
  USD: {
    USD: 1
  }
}
const todayDate = new Date().toISOString().split('T')[0]
// If expired remove items from localStorage
const isExpired = ratesExp && ratesExp !== todayDate
if (isExpired) {
  localStorage.removeItem('rates')
  localStorage.removeItem('currency-exp')
  localStorage.removeItem('selected-currency')
}

export default {
  namespaced: true,
  /** Currency State
   * @typedef {Object} State
   * @property {boolean} currencyRatePending - Currency rate call pending state
   * @property {string} selectedCurrency - User selected currency
   * @property {string} baseCurrency - Base currency for conversion
   * @property {string} externalCurrency - External currency from input, can be set on component base
   * @property {Array<Currency>} currencies - External [currency]{@link Currency} from input, can be set on component base
   * @property {Rates} rates - Conversion rates
   * @property {Array<{message: string, code: number}>} ratesErrors - Conversion errors
   */
  /** @type {State} */
  state: {
    currencyRatePending: false,
    selectedCurrency: ! isExpired && storedCurrency ? storedCurrency : null,
    baseCurrency: null,
    externalCurrency: externalCurrency,
    currencies: storedCurrencies ? JSON.parse(storedCurrencies) : [],
    rates: ! isExpired && storedRates ? JSON.parse(storedRates) : defaultRates,
    ratesErrors: []
  },
  /** Currency Getters
   * @typedef {Object} Getters
   * @property {GetterFunction<string>} defaultCurrency - Default [currency IsoCode]{@link Currency} to use as fallback
   * @property {GetterFunction<string>} currentCurrency - Selected [currency IsoCode]{@link Currency}
   * @property {GetterFunction<function(string): string>} orSelected - Forces the currency in the parameter unless user have manually selected a currency
   * @property {GetterFunction<function(CurrencyQuery): number>} getCurrencyRate - Get conversion rate based on {@link CurrencyQuery}
   * @property {GetterFunction<function(string): Currency>} currencyData - Get [currency data]{@link Currency}
   * @property {GetterFunction<function(CurrencyQuery): number>} getConvertedPrice - Get converted price based on {@link CurrencyQuery}
   * @property {GetterFunction<function(CurrencyQuery): string>} getDisplayPrice - Get price formated to display based on {@link CurrencyQuery}
   * @property {GetterFunction<string>} rateErrorMsg - Currency error message if any
   * @property {GetterFunction<string>} rateErrorCode - Currency error code if any
   */
  /** @type {Getters} */
  getters: {
    defaultCurrency: (state, getters, rootState, rootGetters) => {
      return rootGetters['booking/selectedHotel'] ?. currency ?. isoCode || state.baseCurrency || state.externalCurrency
    },
    currentCurrency: (state, getters, rootState, rootGetters) => {
      return state.selectedCurrency || rootGetters['auth/userCurrency'] || getters.defaultCurrency
    },
    orSelected: (state) => (currency) => state.selectedCurrency || currency,
    getCurrencyRate: (state, getters) => (
      {toCurrency, fromCurrency}
    ) => state.rates[fromCurrency || getters.defaultCurrency] && state.rates[fromCurrency || getters.defaultCurrency][toCurrency || getters.currentCurrency] ? state.rates[fromCurrency || getters.defaultCurrency][toCurrency || getters.currentCurrency] : 1,
    currencyData: (state, getters) => (currency) => state.currencies.find(c => c.IsoCode === (currency || getters.currentCurrency)) || {},
    getConvertedPrice: (state, getters) => (
      {price, fromCurrency, toCurrency}
    ) => ensureNumber(price) * getters.getCurrencyRate(
      {fromCurrency, toCurrency}
    ),
    getDisplayPrice: (state, getters) => (
      {
        price,
        fromCurrency,
        toCurrency,
        withIsoCode = false,
        isoAfter = false,
        noSymbol = false
      }
    ) => {
      const currencyData = getters.currencyData(toCurrency || getters.currentCurrency)
      const isSwiss = currencyData.IsoCode === 'CHF'
      const showNoSymbol = noSymbol || isSwiss
      const showIsoBefore = isSwiss || (withIsoCode && !isoAfter)
      return `${
        showIsoBefore ? currencyData.IsoCode + ' ' : ''
      }${
        showNoSymbol ? '' : currencyData.Symbol || ''
      }${
        formatCurrency(getters.getConvertedPrice({price, toCurrency, fromCurrency}))
      }${
        withIsoCode && isoAfter && ! showIsoBefore ? ' ' + currencyData.IsoCode : ''
      }`
    },
    rateErrorMsg: (state) => state.ratesErrors[0] ?. message,
    rateErrorCode: (state) => state.ratesErrors[0] ?. code
  },
  mutations: {
    'REGISTER_CURRENCIES' ( /** @type {State} */
      state, currencies) {
      state.currencies = currencies
      localStorage.setItem('currencies', JSON.stringify(currencies))
    },
    'CHANGE_CURRENCY' ( /** @type {State} */
      state, selectedCurrency) {
      state.selectedCurrency = selectedCurrency
      if (!selectedCurrency) {
        localStorage.removeItem('selected-currency')
      } else {
        localStorage.setItem('selected-currency', selectedCurrency)
        localStorage.setItem('currency-exp', todayDate)
      }
    },
    'CHANGE_BASE_CURRENCY' ( /** @type {State} */
      state, baseCurrency) {
      state.baseCurrency = baseCurrency
    },
    /**
     * @param {State} state
     * @param {Object} params
     * @param {String} params.rateIso
     * @param {RateResponse} params.rate
     */
    'REGISTER_RATE' (state, {toCurrency, fromCurrency, rate}) {
      if (!state.rates[fromCurrency] || typeof state.rates[fromCurrency] !== 'object') {
        state.rates[fromCurrency] = {
          [fromCurrency]: 1
        }
      }
      if (rate && rate.rates[toCurrency]) {
        state.rates[fromCurrency][toCurrency] = rate.rates[toCurrency]
        localStorage.setItem('rates', JSON.stringify(state.rates))
        localStorage.setItem('currency-exp', rate.date)
      }
    },
    'CURRENCY_CHANGE_PENDING' ( /** @type {State} */
      state, pending) {
      state.currencyRatePending = pending
    },
    'RESPONSE_ERRORS' ( /** @type {State} */
      state, errors) {
      logger.error(errors)
      state.ratesErrors.push({message: 'Something went wrong', code: 404})
    },
    'CLEAR_ERRORS' ( /** @type {State} */
      state) {
      state.ratesErrors = []
      state.externalCurrency = externalCurrency
    }
  },
  /** Auth Actions
   * @typedef {Object} Actions
   * @property {ActionFunction<Array<Currency>,void>} registerCurrencies - Register [currencies]{@link Currency} data in the store
   * @property {ActionFunction<CurrencyQuery,CurrencyQuery>} changeBaseCurrency - Change the base currency for conversions based on {@link CurrencyQuery}
   * @property {ActionFunction<CurrencyQuery|string,void>} changeCurrency - Change the base currency for conversions based on {@link CurrencyQuery}
   * @property {ActionFunction<CurrencyQuery,void>} getRate - Get conversion rate based on {@link CurrencyQuery}
   * @property {ActionFunction<undefined,void>} clearRateErrors - Clear errors
   * @property {ActionFunction<CurrencyQuery,string>} getPriceInCurrency - Get a price in a expecific currency rate based on {@link CurrencyQuery}
   */
  /** @type {Actions} */
  actions: {
    registerCurrencies({
      commit
    }, currencies) {
      commit('REGISTER_CURRENCIES', currencies)
    },
    async changeBaseCurrency(
      {
        state,
        commit,
        getters,
        dispatch
      },
      {fromCurrency, toCurrency}
    ) { // If conversion rate is not available, get new rate
      if (!state.rates[fromCurrency] || !state.rates[fromCurrency][toCurrency || getters.currentCurrency]) {
        await dispatch('getRate', {
          fromCurrency,
          toCurrency: toCurrency || getters.currentCurrency
        })
      }
      if (state.rates[fromCurrency] && state.rates[fromCurrency][toCurrency || getters.currentCurrency]) {
        commit('CHANGE_BASE_CURRENCY', fromCurrency)
      }
      return {fromCurrency, toCurrency}
    },
    async changeCurrency(
      {
        state,
        commit,
        getters,
        dispatch
      },
      params
    ) {
      let fromCurrency = getters.defaultCurrency
      let toCurrency
      if (typeof params === 'string') {
        toCurrency = params
      } else if (typeof params === 'object') {
        toCurrency = params.toCurrency
        fromCurrency = params.fromCurrency ?? getters.defaultCurrency
      }
      // If conversion rate is not available, get new rate
      if (!state.rates[fromCurrency] || !state.rates[fromCurrency][toCurrency]) {
        await dispatch('getRate', {fromCurrency, toCurrency})
      }
      if (state.rates[fromCurrency] && state.rates[fromCurrency][toCurrency]) {
        commit('CHANGE_CURRENCY', toCurrency)
      }
    },
    async getRate(
      {
        state,
        commit
      },
      { fromCurrency, toCurrency }
    ) {
      commit('CLEAR_ERRORS')
      if (state.rates?.[fromCurrency]?.[toCurrency]) {
        return
      }
      if (fromCurrency === toCurrency) {
        commit('REGISTER_RATE', { fromCurrency, toCurrency })
        return
      }
      commit('CURRENCY_CHANGE_PENDING', true)

      const cancelTokenSource = axios.CancelToken.source()

      try {
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => {
            cancelTokenSource.cancel()
            reject(new Error('Currency rate request timed out after 10 seconds'))
          }, 10000)
        })

        const { data } = await Promise.race([
          axios.get(
            getApiUrl(`/api/v1/currency/get-rate/${fromCurrency.toLowerCase()}/${toCurrency.toLowerCase()}`),
            { cancelToken: cancelTokenSource.token }
          ),
          timeoutPromise
        ])

        commit('REGISTER_RATE', { fromCurrency, toCurrency, rate: data })
      } catch (err) {
        commit('RESPONSE_ERRORS', err)
      }
      commit('CURRENCY_CHANGE_PENDING', false)
    },
    clearRateErrors({commit}) {
      commit('CLEAR_ERRORS')
    },
    async getPriceInCurrency(
      {
        state,
        getters,
        dispatch
      },
      {
        price,
        fromCurrency,
        toCurrency,
        symbol,
        format,
        withIsoCode
      }
    ) {
      const haveRate = state.rates[fromCurrency] && state.rates[fromCurrency][toCurrency]
      if (! haveRate && !state.currencyRatePending) {
        logger.debug('get rate')
        await dispatch('getRate', {toCurrency, fromCurrency})
      } else if (state.currencyRatePending) {
        logger.debug('wait for rate')
        await waitForRate(state)
      }
      const convertedPrice = price * getters.getCurrencyRate({fromCurrency, toCurrency})
      return `${
        withIsoCode ? toCurrency + ' ' : ''
      }${symbol}${
        formatCurrency(convertedPrice, format)
      }`
    }
  }
}
